import { deliveryFee, effectivePrice } from '@hunk/shared/api/shop';
import { formatMoney } from '@hunk/shared/common';
import { productPath } from '@hunk/shared/productUrl';
import { Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { Link } from 'react-router';

import { ProductImage } from '@/components/ProductCard';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { useCart } from '@/hooks/useCart';
import { useShop } from '@/hooks/useShop';

export function CartPage() {
  const { items, itemsTotal, setQty, remove } = useCart();
  const { delivery } = useShop();
  const navigate = useAppNavigate();

  if (!items.length)
    return (
      <main className="flex flex-col items-center gap-4 px-4 py-24 text-center">
        <ShoppingBag className="size-16 text-neutral-600" />
        <p className="text-xl font-bold">הסל ריק</p>
        <Link to="/" className="brand-grad rounded-full px-6 py-2.5 font-bold text-white">
          למוצרים
        </Link>
      </main>
    );

  // the paid option that becomes free soonest ("עוד ₪40 ו... חינם!")
  const nextFree = delivery?.options
    .filter((o) => o.freeFrom != null && deliveryFee(o, itemsTotal) > 0)
    .map((o) => ({ name: o.name, missing: (o.freeFrom ?? 0) - itemsTotal }))
    .sort((a, b) => a.missing - b.missing)[0];
  const belowMin = delivery?.minOrder ? delivery.minOrder - itemsTotal : 0;

  return (
    <main className="mx-auto grid max-w-[1100px] gap-6 px-3 py-6 md:grid-cols-[1fr_320px] md:px-4 md:py-10">
      <section>
        <h1 className="mb-4 text-2xl font-extrabold">סל הקניות</h1>
        <ul className="flex flex-col gap-3">
          {items.map(({ product: p, qty }) => (
            <li key={p.id} className="flex gap-3 rounded-2xl border border-white/10 bg-neutral-950 p-3">
              <Link to={productPath(p)} className="size-20 shrink-0 overflow-hidden rounded-xl bg-white p-1 md:size-24">
                <ProductImage src={p.images[0]} alt={p.name} />
              </Link>
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Link to={productPath(p)} className="line-clamp-2 font-semibold hover:underline">
                  {p.name}
                </Link>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center rounded-full border border-white/20">
                    <button type="button" onClick={() => setQty(p.id, qty - 1)} className="p-2" aria-label="פחות">
                      <Minus className="size-3.5" />
                    </button>
                    <span className="w-7 text-center text-sm font-bold">{qty}</span>
                    <button type="button" onClick={() => setQty(p.id, qty + 1)} disabled={qty >= p.maxQty} className="p-2 disabled:opacity-30" aria-label="עוד">
                      <Plus className="size-3.5" />
                    </button>
                  </div>
                  <span className="font-extrabold">{formatMoney(effectivePrice(p) * qty)}</span>
                </div>
              </div>
              <button type="button" onClick={() => remove(p.id)} className="self-start p-1 text-neutral-500 hover:text-red-400" aria-label="הסרה">
                <Trash2 className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <aside className="h-fit rounded-2xl border border-white/10 bg-neutral-950 p-4 md:sticky md:top-28">
        <h2 className="mb-3 font-bold">סיכום</h2>
        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between">
            <dt>מוצרים</dt>
            <dd className="font-semibold">{formatMoney(itemsTotal)}</dd>
          </div>
          {delivery?.options.map((o) => {
            const fee = deliveryFee(o, itemsTotal);
            return (
              <div key={o.id} className="flex justify-between gap-2 text-neutral-400">
                <dt>{o.name}</dt>
                <dd className="whitespace-nowrap">{fee ? formatMoney(fee) : 'חינם'}</dd>
              </div>
            );
          })}
        </dl>
        {nextFree && (
          <p className="mt-3 rounded-lg bg-emerald-500/10 p-2 text-xs text-emerald-400">
            עוד {formatMoney(nextFree.missing)} ו{nextFree.name} חינם!
          </p>
        )}
        {belowMin > 0 && <p className="mt-3 rounded-lg bg-amber-500/10 p-2 text-xs text-amber-400">הזמנה מינימלית {formatMoney(delivery?.minOrder ?? 0)}</p>}
        <button
          type="button"
          disabled={belowMin > 0}
          onClick={() => navigate('/checkout')}
          className="brand-grad brand-grad-hover mt-4 w-full rounded-full py-3 text-base font-bold text-white disabled:opacity-50"
        >
          לתשלום
        </button>
        <Link to="/" className="mt-3 block text-center text-sm text-neutral-400 hover:text-white">
          המשך קנייה
        </Link>
      </aside>
    </main>
  );
}
